import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, Edit, Trash2, ChevronLeft, ChevronRight, FileDown, Copy, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { useAuthStore } from '@/store/auth.store'
import { loanRequestService } from '@/services/loanRequestService'
import { employeesService } from '@/services/employeesService'
import { inboxTasksService } from '@/services/inboxTasksService'
import type { ApprovalStatusItem, Employee, LoanRequestListItem, LoanRequestListResponse } from '@/api/types'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'

function formatEmployeeName(e: Employee, employeeFallback: (id: number) => string): string {
  const name = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
  return name || e.empCode?.trim() || (e.id != null ? employeeFallback(e.id) : '—')
}

/** Department id → display name (Employee API often omits departmentName but sends empDepartmetId) */
async function buildDepartmentNameById(branchId: number): Promise<Record<number, string>> {
  try {
    const list = await loanRequestService.getDepartments(branchId)
    const map: Record<number, string> = {}
    for (const d of list) {
      const name = (d.lookupNameEn || d.nameEn || d.name || d.lookupNameAr || '').trim()
      if (!name) continue
      if (d.id) map[d.id] = name
      const codeId = d.lookupCodeId
      if (codeId != null && codeId > 0) map[codeId] = name
    }
    return map
  } catch {
    return {}
  }
}

function resolveDepartmentName(e: Employee, deptNameById: Record<number, string>): string {
  const direct = (e.departmentName && String(e.departmentName).trim()) || ''
  if (direct) return direct
  const raw =
    e.empDepartmetId ??
    (e as Employee & { empDepartmentId?: number }).empDepartmentId ??
    (e as Employee & { departmentId?: number }).departmentId
  const deptId = raw != null ? Number(raw) : NaN
  if (!Number.isNaN(deptId) && deptId > 0) {
    const resolved = deptNameById[deptId]
    if (resolved) return resolved
  }
  return '—'
}

/** Build map empId → display fields using Employee list + per-id fallback for missing rows */
async function buildEmployeeLookup(
  branchId: number,
  empIds: number[],
  formatEmployeeNameFn: (e: Employee) => string
): Promise<Record<number, { fullName: string; departmentName: string }>> {
  const map: Record<number, { fullName: string; departmentName: string }> = {}
  const unique = [...new Set(empIds.filter((id) => id > 0))]
  if (!unique.length) return map

  const deptNameById = await buildDepartmentNameById(branchId)

  try {
    const empRes = await employeesService.getAllEmployees(branchId, 1, 10000)
    for (const e of empRes.data || []) {
      const id = e.id ?? 0
      if (!id) continue
      map[id] = {
        fullName: formatEmployeeNameFn(e),
        departmentName: resolveDepartmentName(e, deptNameById),
      }
    }
  } catch {
    /* fallback to per-id only */
  }

  const missing = unique.filter((id) => !map[id])
  await Promise.all(
    missing.map(async (id) => {
      try {
        const e = await employeesService.getEmployeeById(branchId, id)
        const eid = e.id ?? id
        map[eid] = {
          fullName: formatEmployeeNameFn(e),
          departmentName: resolveDepartmentName(e, deptNameById),
        }
      } catch {
        /* leave unmapped */
      }
    })
  )

  return map
}

export const LoanRequestList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const employeeFallback = useCallback((id: number) => t('loanRequestPage.employeeFallback', { id }), [t])
  const formatEmployeeNameFn = useCallback(
    (e: Employee) => formatEmployeeName(e, employeeFallback),
    [employeeFallback]
  )

  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [statusCodeType, setStatusCodeType] = useState<string>('0') // send as codeType query param
  const [rows, setRows] = useState<LoanRequestListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [employeeById, setEmployeeById] = useState<
    Record<number, { fullName: string; departmentName: string }>
  >({})

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const list = await inboxTasksService.getApprovalStatuses()
        setStatuses(list || [])
      } catch {
        setStatuses([])
      }
    }
    loadStatuses()
  }, [])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res: LoanRequestListResponse = await loanRequestService.getAll(pageNumber, pageSize, branchId, statusCodeType || '0')
        const list = res.data || []
        setRows(list)
        setTotalRecords(res.totalRecords || 0)

        const empIds = list.map((r) => r.empId).filter((id): id is number => id != null && id > 0)
        try {
          const lookup = await buildEmployeeLookup(branchId, empIds, formatEmployeeNameFn)
          setEmployeeById(lookup)
        } catch {
          setEmployeeById({})
        }
      } catch (e: any) {
        const msg = (e?.response?.data?.message || e?.message || t('loanRequestPage.fetchFailed')).toString()
        const isNoData = /not found|no data|no records/i.test(msg)
        setRows([])
        setTotalRecords(0)
        setEmployeeById({})
        if (!isNoData) toast.error(msg)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId, pageNumber, pageSize, statusCodeType, formatEmployeeNameFn])

  const rowsWithEmployee = useMemo(() => {
    return rows.map((r) => {
      const emp = r.empId ? employeeById[r.empId] : undefined
      const employeeName = (emp?.fullName || r.employeeName || '').trim() || '—'
      const departmentName = (emp?.departmentName || r.departmentName || '').trim() || '—'
      return { ...r, employeeName, departmentName }
    })
  }, [rows, employeeById])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rowsWithEmployee
    const q = searchQuery.toLowerCase()
    return rowsWithEmployee.filter((r) => {
      return (
        (r.loanNo ?? '').toLowerCase().includes(q) ||
        (r.employeeName ?? '').toLowerCase().includes(q) ||
        (r.departmentName ?? '').toLowerCase().includes(q) ||
        String(r.loanAmount ?? '').toLowerCase().includes(q) ||
        String(r.statusName ?? '').toLowerCase().includes(q)
      )
    })
  }, [rowsWithEmployee, searchQuery])

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return String(dateStr).slice(0, 10)
    }
  }

  const statusBadge = (row: LoanRequestListItem) => {
    const statusCode = Number(row.status ?? 0)
    let text: string
    if (statusCode === 434) text = t('loanRequestPage.statusPending')
    else if (statusCode === 435) text = t('loanRequestPage.statusApproved')
    else if (statusCode === 436) text = t('loanRequestPage.statusRejected')
    else text = row.statusName || (row.status != null ? String(row.status) : '—')

    const isApproved = statusCode === 435
    const isPending = statusCode === 434
    return (
      <Badge
        variant={isApproved ? 'default' : isPending ? 'secondary' : 'outline'}
        className={isApproved ? 'text-xs' : 'text-xs'}
      >
        {text}
      </Badge>
    )
  }

  const csvEscape = (cell: string | number) => `"${String(cell).replace(/"/g, '""')}"`

  const handleCopy = async () => {
    if (filteredRows.length === 0) {
      toast.error(t('loanRequestPage.nothingToCopy', { defaultValue: 'Nothing to copy.' }))
      return
    }
    const header = [
      t('loanRequestPage.colLoanNo'),
      t('loanRequestPage.colEmployee'),
      t('loanRequestPage.colDepartment'),
      t('loanRequestPage.colStartDate'),
      t('loanRequestPage.colLoanAmount'),
      t('loanRequestPage.colNoOfInstallment'),
      t('loanRequestPage.colDeductAmount'),
      t('loanRequestPage.colStatus'),
    ].join('\t')
    const lines = filteredRows.map((r) =>
      [
        r.loanNo ?? '',
        r.employeeName ?? '',
        r.departmentName ?? '',
        formatDate(r.startDate),
        r.loanAmount ?? '',
        r.noOfInstallment ?? '',
        r.deductAmount ?? '',
        String(r.statusName ?? r.status ?? ''),
      ].join('\t')
    )
    await navigator.clipboard.writeText([header, ...lines].join('\n'))
    toast.success(t('loanRequestPage.copiedToClipboard', { defaultValue: 'Copied to clipboard.' }))
  }

  const handleExportCSV = () => {
    if (filteredRows.length === 0) {
      toast.error(t('loanRequestPage.nothingToExport', { defaultValue: 'Nothing to export.' }))
      return
    }
    const header = [
      t('loanRequestPage.colLoanNo'),
      t('loanRequestPage.colEmployee'),
      t('loanRequestPage.colDepartment'),
      t('loanRequestPage.colStartDate'),
      t('loanRequestPage.colLoanAmount'),
      t('loanRequestPage.colNoOfInstallment'),
      t('loanRequestPage.colDeductAmount'),
      t('loanRequestPage.colStatus'),
    ]
      .map(csvEscape)
      .join(',')
    const body = filteredRows
      .map((r) =>
        [
          r.loanNo ?? '',
          r.employeeName ?? '',
          r.departmentName ?? '',
          formatDate(r.startDate),
          r.loanAmount ?? '',
          r.noOfInstallment ?? '',
          r.deductAmount ?? '',
          String(r.statusName ?? r.status ?? ''),
        ]
          .map(csvEscape)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `loan_requests_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('loanRequestPage.csvDownloaded', { defaultValue: 'CSV downloaded.' }))
  }

  const handlePrint = () => window.print()

  const approvedStatusId = statuses.find((s) => String(s.lookupNameEn || '').toLowerCase() === 'approved')?.id

  const isApprovedLoan = (row: LoanRequestListItem) => {
    if (approvedStatusId && row.status === approvedStatusId) return true
    if (String(row.statusName || '').toLowerCase() === 'approved') return true
    return Number(row.status) === 435
  }

  const handleDelete = async (row: LoanRequestListItem) => {
    if (isApprovedLoan(row)) {
      await showError(t('loanRequestPage.deleteFailedTitle'), t('loanRequestPage.cannotDeleteApproved'))
      return
    }
    const confirmed = await showDeleteConfirm(undefined, t('loanRequestPage.deleteConfirm'))
    if (!confirmed) return
    try {
      await loanRequestService.delete(row.id)
      await showSuccess(t('loanRequestPage.deletedTitle'), t('loanRequestPage.deletedMessage'))
      if (branchId) {
        const res = await loanRequestService.getAll(pageNumber, pageSize, branchId, statusCodeType || '0')
        const list = res.data || []
        setRows(list)
        setTotalRecords(res.totalRecords || 0)
        const empIds = list.map((r) => r.empId).filter((id): id is number => id != null && id > 0)
        try {
          const lookup = await buildEmployeeLookup(branchId, empIds, formatEmployeeNameFn)
          setEmployeeById(lookup)
        } catch {
          setEmployeeById({})
        }
      }
    } catch (e: any) {
      await showError(
        t('loanRequestPage.deleteFailedTitle'),
        e?.response?.data?.message ?? e?.response?.data?.result?.msg ?? e?.message ?? t('loanRequestPage.deleteFailedMessage')
      )
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('loanRequestPage.pageTitle')} description={t('loanRequestPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState title={t('loanRequestPage.branchRequiredTitle')} description={t('loanRequestPage.branchRequiredDescription')} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('loanRequestPage.pageTitle')}
        description={t('loanRequestPage.pageDescription')}
        action={{
          label: t('loanRequestPage.addRequest'),
          onClick: () => navigate('/self-services/loan-requests/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('loanRequestPage.labelApprovalStatuses')}</label>
                <Select
                  value={statusCodeType || '0'}
                  onValueChange={(v) => {
                    setStatusCodeType(v)
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder={t('loanRequestPage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('loanRequestPage.placeholderAll')}</SelectItem>
                    {statuses
                      .filter((s) => (s.lookupCodeId ?? s.id) != null)
                      .map((s) => {
                        const value = String(s.lookupCodeId ?? s.id ?? 0)
                        const label = s.lookupNameEn || s.lookupNameAr || String(value)
                        return (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        )
                      })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('loanRequestPage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <FileDown className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t('loanRequestPage.copy', { defaultValue: 'Copy' })}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  {t('loanRequestPage.print', { defaultValue: 'Print' })}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colLoanNo')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colEmployee')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colDepartment')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colStartDate')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colLoanAmount')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colNoOfInstallment')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colDeductAmount')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('loanRequestPage.colStatus')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary w-28">
                      {t('loanRequestPage.colAction')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                        {searchQuery ? t('loanRequestPage.noMatchSearch') : t('loanRequestPage.noRequestsFound')}
                      </TableCell>
                    </TableRow>
                  ) : filteredRows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                    >
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.loanNo ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.employeeName ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.departmentName ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.startDate)}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.loanAmount ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.noOfInstallment ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.deductAmount ?? '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">{statusBadge(row)}</TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <div className="flex justify-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-primary hover:bg-primary/10"
                            onClick={() => navigate(`/self-services/loan-requests/${row.id}`)}
                            title={t('loanRequestPage.editTitle')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {!isApprovedLoan(row) ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(row)}
                            title={t('loanRequestPage.deleteTitle')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('loanRequestPage.pageSize')}</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v))
                      setPageNumber(1)
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('loanRequestPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                    <ChevronLeft className="h-4 w-4" />
                    {t('loanRequestPage.previous')}
                  </Button>
                  <span className="text-sm">{t('loanRequestPage.pageOf', { page: pageNumber, totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))} disabled={pageNumber >= totalPages}>
                    {t('loanRequestPage.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
